// iso-date-fns
// TODO: maybe curry all the things?
import {
  Interval,
  add,
  addDays as addDaysDF,
  addHours as addHoursDF,
  addMinutes as addMinutesDF,
  addMonths as addMonthsDF,
  addWeeks as addWeeksDF,
  closestIndexTo as closestIndexToDF,
  compareAsc as compareAscDF,
  compareDesc as compareDescDF,
  differenceInDays as differenceInDaysDF,
  differenceInMinutes as differenceInMinutesDF,
  differenceInSeconds as differenceInSecondsDF,
  differenceInWeeks as differenceInWeeksDF,
  endOfTomorrow as endOfTomorrowDF,
  formatDistance as formatDistanceDF,
  formatISO,
  getDate as getDateDF,
  getDay as getDayDF,
  getHours as getHoursDF,
  getTime as getTimeDF,
  hoursToMinutes,
  hoursToSeconds,
  isAfter as isAfterDF,
  isBefore as isBeforeDF,
  isEqual,
  isMatch,
  isPast as isPastDF,
  isToday as isTodayDF,
  isValid as isValidDF,
  minutesToSeconds,
  parse,
  parseISO,
  roundToNearestMinutes as roundToNearestMinutesDF,
  secondsToMinutes,
  set,
  startOfHour as startOfHourDF,
  startOfISOWeek as startOfISOWeekDF,
  startOfMonth as startOfMonthDF,
  startOfToday as startOfTodayDF,
  startOfYear as startOfYearDF,
  startOfYesterday as startOfYesterdayDF,
} from 'date-fns';
import {
  OptionsWithTZ,
  format as formatDFT,
  formatInTimeZone,
  utcToZonedTime as utcToZonedTimeDFT,
} from 'date-fns-tz';
import {enUS} from 'date-fns/locale';
import {curry, has, mapObjIndexed, omit, pickBy} from 'ramda';
import {Validator, isPojo, isString} from '../Util';

export {hoursToSeconds, hoursToMinutes, minutesToSeconds, secondsToMinutes, formatISO as dateToIso};
// Timestamp is a string of the type '2020-02-07T21:45:50-02:00'
// TODO: make Timestamp an opaque type (so we can preserve validations)
export type Timestamp = string;
export type Timezone = string;
type Hours = `0${IntRange<0, 10>}` | `${IntRange<10, 24>}`;
type Minutes = `0${IntRange<0, 10>}` | `${IntRange<10, 60>}`;
type Seconds = Minutes;
export type Time = `${Hours}:${Minutes}:${Seconds}`;
export const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;
export type WeekDay = (typeof WEEKDAYS)[number];
// Interval is valid if startDate is less than or equal to endDate
export type TimestampInterval = {
  startDate: Timestamp;
  endDate: Timestamp;
};
export type MaybeOpenInterval =
  | TimestampInterval
  | {
      startDate?: Maybe<Timestamp>;
      endDate?: Maybe<Timestamp>;
    };
export function isMaybeOpenInterval(o: unknown): o is MaybeOpenInterval {
  if (!isPojo(o)) return false;
  return Object.keys(o).every(v => ['startDate', 'endDate'].includes(v));
}

export const DATE_RENDERING_FORMAT = 'MM/dd/yyyy';
export const TIME_WITHOUT_TIMEZONE_NAME_FORMAT = 'h:mm a';
export const TIME_RENDERING_FORMAT = 'h:mm a zzz';
export const DATE_TIME_RENDERING_FORMAT = `${DATE_RENDERING_FORMAT} ${TIME_RENDERING_FORMAT}`;
export const FULL_DATE_TIME_FORMAT = "cccc, MMMM do, yyyy, 'at' h:mm a";
export const DATE_ISO_FORMATTING = `yyyy-MM-dd'T'HH:mm:ssxxx`;
export const WEEKDAY_NAME_FORMAT = 'eeee';
export const VALID_TIMESTAMP_REGEX =
  /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(-0[1-9]|-1[0-2]|\+0[0-9]|\+1[0-4]):\d{2}/;
const formatsWithTimezone = [`yyyy-MM-dd'T'HH:mm:ssx`, DATE_ISO_FORMATTING];
export const timestampAcceptedFormats = [
  'E',
  'EEEE',
  'k:mm',
  'hh:mma',
  'hh:mm a',
  'hh:mmaaa',
  'hh:mm aaa',
  TIME_RENDERING_FORMAT,
  'MM/dd',
  'MM/dd k:mm',
  'MM/dd hh:mma',
  'MM/dd hh:mm a',
  'MM/dd hh:mmaaa',
  'MM/dd hh:mm aaa',
  'MM/dd/yy',
  DATE_RENDERING_FORMAT,
  'yyyy-MM-dd',
  `${DATE_RENDERING_FORMAT} k:mm`,
  `${DATE_RENDERING_FORMAT} h:mma`,
  `${DATE_RENDERING_FORMAT} h:mm a`,
  `${DATE_RENDERING_FORMAT} hh:mma`,
  `${DATE_RENDERING_FORMAT} hh:mm a`,
  `${DATE_RENDERING_FORMAT} h:mmaaa`,
  `${DATE_RENDERING_FORMAT} hh:mmaaa`,
  `${DATE_RENDERING_FORMAT} h:mm aaa`,
  `${DATE_RENDERING_FORMAT} hh:mm aaa`,
  DATE_TIME_RENDERING_FORMAT,
  'MMM',
  'MMM do',
  'MMM do, yyyy',
  'MMM do, yyyy k:mm',
  'MMM do, yyyy hh:mma',
  'MMM do, yyyy hh:mm a',
  'MMM do, yyyy hh:mmaaa',
  'MMM do, yyyy hh:mm aaa',
  'MMMM',
  'MMMM do',
  'MMMM do, yyyy',
  'MMMM do, yyyy k:mm',
  'MMMM do, yyyy hh:mma',
  'MMMM do, yyyy hh:mm a',
  'MMMM do, yyyy hh:mmaaa',
  'MMMM do, yyyy hh:mm aaa',
  FULL_DATE_TIME_FORMAT,
  ...formatsWithTimezone,
];
export const DEFAULT_TIMEZONE = (() => {
  try {
    return process.env.TZ || 'America/Los_Angeles';
  } catch (_error) {
    // Browser
    return 'America/Los_Angeles';
  }
})();
export const weekDays: WeekDay[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];
export const HOURS_TO_MINUTES = 60;
export const MINUTES_TO_SECONDS = 60;

/* ************************* Date manipulation ************************* */
export const roundToNearestMinutes = /* @__PURE__ */ wrapDateFNS(roundToNearestMinutesDF);
export const addTime = /* @__PURE__ */ wrapDateFNS(add);
export const addMinutes = /* @__PURE__ */ wrapDateFNS(addMinutesDF);
export const addHours = /* @__PURE__ */ wrapDateFNS(addHoursDF);
export const addDays = /* @__PURE__ */ wrapDateFNS(addDaysDF);
export const addWeeks = /* @__PURE__ */ wrapDateFNS(addWeeksDF);
export const addMonths = /* @__PURE__ */ wrapDateFNS(addMonthsDF);
export const differenceInSeconds = /* @__PURE__ */ wrapDateFNS(differenceInSecondsDF);
export const differenceInMinutes = /* @__PURE__ */ wrapDateFNS(differenceInMinutesDF);
export const differenceInDays = /* @__PURE__ */ wrapDateFNS(differenceInDaysDF);
export const differenceInWeeks = /* @__PURE__ */ wrapDateFNS(differenceInWeeksDF);
export const startOfToday = /* @__PURE__ */ wrapDateFNS(startOfTodayDF);
export const endOfTomorrow = /* @__PURE__ */ wrapDateFNS(endOfTomorrowDF);
export const startOfYear = /* @__PURE__ */ wrapDateFNS(startOfYearDF);
export const startOfMonth = /* @__PURE__ */ wrapDateFNS(startOfMonthDF);
export const startOfYesterday = /* @__PURE__ */ wrapDateFNS(startOfYesterdayDF);
export const startOfISOWeek = /* @__PURE__ */ wrapDateFNS(startOfISOWeekDF);
export const startOfHour = /* @__PURE__ */ wrapDateFNS(startOfHourDF);
export const getTime = /* @__PURE__ */ wrapDateFNS(getTimeDF);
export const getHours = /* @__PURE__ */ wrapDateFNS(getHoursDF);
export const getDate = /* @__PURE__ */ wrapDateFNS(getDateDF);
export const setTimestamp = /* @__PURE__ */ wrapDateFNS(set);
export const closestIndexTo = /* @__PURE__ */ wrapDateFNS(closestIndexToDF);
export const formatDistance = /* @__PURE__ */ wrapDateFNS(formatDistanceDF);

export const isoToDate = (timestamp: Timestamp): Date => parseISO(timestamp);
export const isoNow = (): Timestamp =>
  formatDFT(utcToZonedTimeDFT(new Date(), DEFAULT_TIMEZONE), DATE_ISO_FORMATTING, {
    timeZone: DEFAULT_TIMEZONE,
  });
export const now = (): string => formatTimestamp(isoNow(), {format: 'yyyy-MM-dd HH:mm:ss.SSS'});
export const orNow = (timestamp?: Maybe<Timestamp>): Timestamp => timestamp || isoNow();
export const at8am = (timestamp: Maybe<Timestamp>): Timestamp =>
  setTimestamp(timestamp || isoNow(), {
    hours: 8,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
export function maxTimestamp(timestamps: Timestamp[]): Maybe<Timestamp> {
  if (timestamps.length === 0) return null;
  let result = timestamps[0];
  timestamps.forEach(t => {
    if (isBefore(result, t)) result = t;
  });
  return result;
}
export function splitTimestamp(t: Timestamp): {date: string; time: Time; timezoneOffset: string} {
  const [date, timeWithTz] = t.split('T');
  const time = timeWithTz.slice(0, 8) as Time;
  const timezoneOffset = timeWithTz.slice(8);
  return {date, time, timezoneOffset};
}
export const stripTimezone = (timestamp: Timestamp): string => timestamp.slice(0, 19);
export const getTimezoneOffset = (timezone: Timezone) =>
  formatTimestamp(isoNow(), {timezone, format: 'xxx'});
export const replaceTimezone = (timestamp: Timestamp, timezone: Timezone): Timestamp =>
  stripTimezone(timestamp) + formatTimestamp(timestamp, {timezone, format: 'xxx'});
export const convertTimestampToTimezone = (timestamp: Timestamp, timezone: string): Timestamp =>
  formatInTimeZone(parseISO(timestamp), timezone, DATE_ISO_FORMATTING);
export const fromUtc = (string: string): string => formatISO(parseISO(string + '+00:00'));
export const toUtc = <T extends Timestamp | null | undefined>(timestamp: T): T =>
  //@ts-ignore
  !timestamp ? timestamp : convertTimestampToTimezone(timestamp, 'utc');
export const toUtcOrNowUtc = (string: Maybe<string>): Timestamp => toUtc(string) || toUtc(isoNow());
export function stringToTime(str: string): Time {
  const [h, m, timeRest] = str.split(':');
  const s = timeRest ? timeRest.slice(0, 2) : '00';
  return `${parseInt(h) < 10 ? `0${parseInt(h)}` : h}:${m ?? '00'}:${s}` as Time;
}
export function convertTimeToTimezone({
  time,
  currentTimezone,
  targetTimezone,
}: {
  time: Time;
  currentTimezone: Timezone;
  targetTimezone: Timezone;
}): Time {
  const [date, _timeNow] = isoNow().split('T');
  const convertedTimestamp = convertTimestampToTimezone(
    `${date}T${time}${getTimezoneOffset(currentTimezone)}`,
    targetTimezone,
  );
  return convertedTimestamp.split('T')[1].slice(0, 8) as Time;
}
export const undefinedSafeToUtc = <T extends Timestamp | null | undefined>(string: T): T =>
  //@ts-ignore
  string === undefined ? undefined : toUtc(string);
export function rubyTimestamp<T extends Maybe<string>>(timestamp: T | Date): Maybe<Timestamp> {
  // @ts-ignore ternary not working properly
  return timestamp
    ? formatTimestamp(timestamp instanceof Date ? timestamp.toISOString() : timestamp, {
        format: DATE_ISO_FORMATTING,
      })
    : timestamp;
}
export function createIsoTimestamp(date: string, time: string, timezone: string): string {
  const timestamp = [date.replace(/\//g, '-'), convert12HourTimeTo24HourTime(time)].join('T');
  return formatDFT(parseISO(timestamp), DATE_ISO_FORMATTING, {
    timeZone: timezone,
  });
}
export const parseDate: (arg0: string, arg1: string, arg2: Date) => string = (
  dateToParse,
  formatString,
  referenceDate,
) => {
  return formatISO(parse(dateToParse, formatString, referenceDate));
};
export function safeTimestampParse(timestamp: unknown, timezone: string): Timestamp | null {
  if (typeof timestamp !== 'string') return null;
  if (!timezone) return null;
  const isValidFormat = (format: string) => isTimestampInFormat(timestamp, format);
  const applicableFormat = timestampAcceptedFormats.find(isValidFormat);
  if (!applicableFormat) return null;
  const parsedTimestamp = parseDate(timestamp, applicableFormat, new Date());
  try {
    if (formatsWithTimezone.includes(applicableFormat))
      return convertTimestampToTimezone(parsedTimestamp, timezone);
    return replaceTimezone(parsedTimestamp, timezone);
  } catch (_error) {
    return null;
  }
}
export const daysToSeconds = (days: number): number => 60 * 60 * 24 * days;
export function convert12HourTimeTo24HourTime(time: Maybe<string>) {
  if (!time) return '';
  const [hours, minutes, maybeSeconds] = time.replace('AM', '').replace('PM', '').trim().split(':');
  const seconds = maybeSeconds || '00';
  if (time.includes('AM')) {
    if (hours === '12') return `00:${minutes}:${seconds}`;
    return `${hours.length === 1 ? `0${hours}` : hours}:${minutes}:${seconds}`;
  }
  if (hours === '12') return `${hours}:${minutes}:${seconds}`;
  return `${parseInt(hours) + 12}:${minutes}:${seconds}`;
}
export const getWeekDayName = (t: Timestamp, timezone?: Timezone): WeekDay =>
  formatTimestamp(t, {format: WEEKDAY_NAME_FORMAT, timezone}).toLowerCase() as WeekDay;
export const getDayOfTheWeek = (t: Timestamp): number =>
  wrapDateFNS(getDayDF)(stripTimezone(t) + getTimezoneOffset(DEFAULT_TIMEZONE));
export function timeToTimestamp(t: Time, baseDate?: Timestamp): Timestamp {
  const {date, timezoneOffset} = splitTimestamp(
    baseDate || convertTimestampToTimezone(isoNow(), DEFAULT_TIMEZONE),
  );
  return `${date}T${t}${timezoneOffset}`;
}
export const timeSum = (t: Time, secondsToAdd: number): Time =>
  stripTimezone(
    convertTimestampToTimezone(
      addTime(timeToTimestamp(t), {seconds: secondsToAdd}),
      DEFAULT_TIMEZONE,
    ),
  ).split('T')[1] as Time;
export const timeDifferenceInSeconds = (left: Time, right: Time): number =>
  differenceInSeconds(timeToTimestamp(left), timeToTimestamp(right));
export function startOfDay(t: Timestamp): Timestamp {
  const splitted = splitTimestamp(t);
  return splitted.date + 'T00:00:00' + splitted.timezoneOffset;
}
export function endOfDay(t: Timestamp): Timestamp {
  const splitted = splitTimestamp(t);
  return splitted.date + 'T23:59:59' + splitted.timezoneOffset;
}

/* ************************* Date validation ************************* */
export const isValidDate = /* @__PURE__ */ wrapDateFNS(isValidDF);
export const isBefore = /* @__PURE__ */ wrapDateFNS(isBeforeDF);
export const isAfter = /* @__PURE__ */ wrapDateFNS(isAfterDF);
export const isPast = /* @__PURE__ */ wrapDateFNS(isPastDF);
export const equalTimestamps = /* @__PURE__ */ wrapDateFNS(isEqual);
export const compareTimeAsc = /* @__PURE__ */ wrapDateFNS(compareAscDF);
export const compareTimeDesc = /* @__PURE__ */ wrapDateFNS(compareDescDF);
const wrappedIsToday = /* @__PURE__ */ wrapDateFNS(isTodayDF);

export const isToday = (timestamp: Maybe<Timestamp>) => !!timestamp && wrappedIsToday(timestamp);
export const isAfterOrEqual = (timestamp: Timestamp, timestamp2: Timestamp): boolean =>
  isAfter(timestamp, timestamp2) || equalTimestamps(timestamp, timestamp2);
export const isBeforeOrEqual = (dateA: Timestamp, dateB: Timestamp): boolean =>
  isBefore(dateA, dateB) || equalTimestamps(dateA, dateB);
export const isBetweenOrEqual = (timestamp: Timestamp, start: Timestamp, end: Timestamp): boolean =>
  isAfterOrEqual(timestamp, start) && isAfterOrEqual(end, timestamp);
export const isValidTimestamp = (timestamp: Maybe<string>): timestamp is Timestamp =>
  isTimestampInFormat(timestamp, DATE_ISO_FORMATTING);
export const validateTimestamp = (timestamp: Maybe<Timestamp>): Maybe<string> =>
  !timestamp ? 'is required' : !isValidTimestamp(timestamp) ? 'is invalid' : null;

/* ************************* Date formatting ************************* */
type FormatOptions = Omit<OptionsWithTZ, 'timeZone'>;
export const formatTimestamp: <T extends Timestamp | null | undefined>(
  timestamp: T,
  options?: {format?: string; timezone?: Timezone} & FormatOptions,
) => T = (timestamp, options) =>
  // @ts-ignore
  timestamp
    ? formatDFT(
        utcToZonedTimeDFT(timestamp, options?.timezone || DEFAULT_TIMEZONE),
        options?.format || DATE_TIME_RENDERING_FORMAT,
        {
          locale: enUS,
          ...options,
          timeZone: options?.timezone || DEFAULT_TIMEZONE,
        },
      )
    : timestamp;
export const renderDate = (t: Maybe<Timestamp>, timezone?: Timezone): string =>
  t ? formatTimestamp(t, {format: DATE_RENDERING_FORMAT, timezone}) : '';
export const renderTime = (t: Maybe<Timestamp>, timezone?: Timezone): string =>
  t ? formatTimestamp(t, {format: TIME_RENDERING_FORMAT, timezone}) : '';
export const renderRangeTime = (t1: Timestamp, t2: Timestamp, timezone?: Timezone): string =>
  `${formatTimestamp(t1, {
    format: TIME_WITHOUT_TIMEZONE_NAME_FORMAT,
    timezone,
  })} - ${formatTimestamp(t2, {
    format: TIME_RENDERING_FORMAT,
    timezone,
  })}`;
export const renderTimeWithoutTimezoneName = (t: Maybe<Timestamp>, timezone?: Timezone): string =>
  t ? formatTimestamp(t, {format: TIME_WITHOUT_TIMEZONE_NAME_FORMAT, timezone}) : '';
export const renderDateTime = (t: Maybe<Timestamp>, timezone?: string): string =>
  t
    ? formatTimestamp(t, {
        format: DATE_TIME_RENDERING_FORMAT,
        timezone,
      })
    : '';
export const renderFullDateTime = (t: Maybe<Timestamp>, timezone?: string): string =>
  t
    ? formatTimestamp(t, {
        format: FULL_DATE_TIME_FORMAT,
        timezone,
      })
    : '';
export const today = (): string => renderDate(isoNow());
export function isTimestampInFormat(timestamp: Maybe<string>, format: Maybe<string>): boolean {
  if (!timestamp || !format) return false;
  try {
    return isMatch(timestamp, format);
  } catch (_error) {
    return false;
  }
}
export function formatDuration(inputInMinutes: number): string {
  const oneDay = 24 * 60;
  const days = Math.floor(inputInMinutes / oneDay);
  const hours = Math.floor((inputInMinutes % oneDay) / 60);
  const minutes = (inputInMinutes % oneDay) % 60;
  const plural = unit => (unit > 1 ? 's' : '');
  return [
    days && `${days} day${plural(days)}`,
    hours && `${hours} hr${plural(hours)}`,
    minutes && `${minutes} min${plural(minutes)}`,
  ]
    .filter(Boolean)
    .join(' ');
}
export const getTimezoneName = ({
  timezone,
  abbreviated = true,
}: {
  timezone: Timezone;
  abbreviated?: boolean;
}): string =>
  formatInTimeZone(isoNow(), timezone, abbreviated ? 'zzz' : 'zzzz', {
    locale: enUS,
  });

/* ************************* Date interval ************************* */
export const dayInterval = (timestamp: Timestamp): TimestampInterval => ({
  startDate: startOfDay(timestamp),
  endDate: endOfDay(timestamp),
});
export const previousDayInterval = (t: Timestamp): TimestampInterval => dayInterval(addDays(t, -1));
export const nextDayInterval = (t: Timestamp): TimestampInterval => dayInterval(addDays(t, 1));
export function lastDaysInterval(n: number, t?: Timestamp): TimestampInterval {
  const now = t || isoNow();
  return {
    startDate: startOfDay(addDays(now, 1 - n)),
    endDate: endOfDay(now),
  };
}
export const isInInterval = (timestamp: Timestamp, interval: TimestampInterval): boolean =>
  isAfterOrEqual(timestamp, interval.startDate) && isAfter(interval.endDate, timestamp);
export const mergeIntervals = (intervals: TimestampInterval[]): TimestampInterval => ({
  startDate: intervals[0].startDate,
  endDate: intervals[intervals.length - 1].endDate,
});
export function getIntervalIntersection(
  firstInterval: TimestampInterval,
  secondInterval: TimestampInterval,
): TimestampInterval | null {
  if (
    isAfter(secondInterval.startDate, firstInterval.endDate) ||
    isAfter(firstInterval.startDate, secondInterval.endDate)
  )
    return null;
  return {
    startDate: isAfter(firstInterval.startDate, secondInterval.startDate)
      ? firstInterval.startDate
      : secondInterval.startDate,
    endDate: isAfter(firstInterval.endDate, secondInterval.endDate)
      ? secondInterval.endDate
      : firstInterval.endDate,
  };
}
export const calculateIntervalDurationInMinutes = (i: TimestampInterval) =>
  differenceInMinutes(i.endDate, i.startDate);
export function formatTimeInterval(interval: MaybeOpenInterval): string {
  const {startDate, endDate} = interval;
  return startDate && endDate
    ? `between ${formatTimestamp(startDate, {
        format: DATE_TIME_RENDERING_FORMAT,
      })} and ${formatTimestamp(endDate, {format: DATE_TIME_RENDERING_FORMAT})}`
    : startDate
    ? `after ${formatTimestamp(startDate, {
        format: DATE_TIME_RENDERING_FORMAT,
      })}`
    : endDate
    ? `before ${formatTimestamp(endDate, {format: DATE_TIME_RENDERING_FORMAT})}`
    : '';
}

/* ************************* Wrapper ************************* */
type Primitive = string | number | symbol | boolean | Date | Interval | TimestampInterval;
type PrimitiveReplace<T> = unknown extends T
  ? unknown
  : Date extends T
  ? Timestamp
  : Interval extends T
  ? TimestampInterval
  : T;
type TupleReplace<A extends unknown[], T extends unknown[] = [], I extends unknown[] = []> = {
  0: TupleReplace<
    A,
    Cast<Append<T, RecursiveReplaceDates<A[Length<I>]>>, unknown[]>,
    Cast<Append<I, unknown>, unknown[]>
  >;
  1: T;
}[Length<T> extends Length<A> ? 1 : 0];
type ObjectReplace<T extends Record<string | number | symbol, unknown>> = Cast<
  {
    [P in keyof T]: T[P] extends Primitive ? PrimitiveReplace<T[P]> : RecursiveReplaceDates<T[P]>;
  },
  Record<string | number | symbol, unknown>
>;
type ArrayReplace<T extends unknown[]> = Head<T> extends never
  ? T extends (infer U)[]
    ? PrimitiveReplace<U>[]
    : never
  : TupleReplace<T>;
type ArgsReplace<A> = Cast<{[P in keyof A]: RecursiveReplaceDates<A[P]>}, unknown[]>;
type FunctionReplace<
  // eslint-disable-next-line
  T extends (...args: any[]) => unknown,
> = Parameters<T> extends []
  ? () => RecursiveReplaceDates<ReturnType<T>>
  : // @ts-ignore
    (...args: ArgsReplace<Parameters<T>>) => RecursiveReplaceDates<ReturnType<T>>;
type RecursiveReplaceDates<T> = T extends Primitive
  ? PrimitiveReplace<T>
  : // eslint-disable-next-line
  T extends (...args: any[]) => unknown
  ? FunctionReplace<T>
  : T extends unknown[]
  ? ArrayReplace<T>
  : T extends Record<string | number | symbol, unknown>
  ? ObjectReplace<T>
  : PrimitiveReplace<T>;
// eslint-disable-next-line
export function wrapDateFNS<Fn extends (...args: any[]) => unknown>(
  fn: Fn,
): RecursiveReplaceDates<Fn> {
  const timestampToDate = (a: unknown) => (isString(a) ? parseISO(a) : a);
  const dateToTimestamp = (a: unknown) => (a instanceof Date ? formatISO(a) : a);
  const curriedWrap = curry(wrap);
  //@ts-ignore
  return (...args) =>
    args.some(Validator.isNullUndefinedEmptyString)
      ? null
      : wrap(dateToTimestamp, fn(...wrap(timestampToDate, args)));
  function wrap(convertionFn: (arg: unknown) => unknown, toWrap: unknown) {
    if (Array.isArray(toWrap)) return toWrap.map(something => wrap(convertionFn, something));
    if (isPojo(toWrap))
      return mapObjIndexed(
        //@ts-ignore
        curriedWrap(convertionFn),
        convertIfInterval(toWrap),
      );
    return convertionFn(toWrap);
  }
  function convertIfInterval(interval: Record<string | number | symbol, unknown>) {
    const omitted = omit(['startDate', 'endDate', 'start', 'end'], interval);
    const toAdd: Record<string, unknown> = pickBy(Boolean, {
      start: has('startDate', interval) ? interval.startDate : null,
      end: has('endDate', interval) ? interval.endDate : null,
      startDate: has('start', interval) ? interval.start : null,
      endDate: has('end', interval) ? interval.end : null,
    });
    return {...omitted, ...toAdd};
  }
}
export function formatHourToHourMinutes(hour: number): string {
  const minutes = Math.ceil(hoursToMinutes(hour));
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return [h && `${h}h`, m && `${m}min`].filter(Boolean).join(' ');
}
export function format24HourToAmPMHour(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  const minutes = Math.floor((hour - Math.floor(hour)) * 60);
  return hour < 12
    ? `${Math.floor(hour)}${minutes > 0 ? `:${minutes}` : ''} AM`
    : `${Math.floor(hour) - 12}${minutes > 0 ? `:${minutes}` : ''} PM`;
}
const MILLISSECONDS_IN_MINUTE = 60 * 1000;
const MILLISSECONDS_IN_HOUR = 60 * MILLISSECONDS_IN_MINUTE;
const MILLISECONDS_IN_DAY = MILLISSECONDS_IN_HOUR * 24;
export function formatUnixInterval(argInterval: number) {
  const interval = Math.abs(argInterval);
  const days = Math.floor(interval / MILLISECONDS_IN_DAY);
  const hours = Math.floor((interval % MILLISECONDS_IN_DAY) / MILLISSECONDS_IN_HOUR);
  const minutes = Math.floor((interval % MILLISSECONDS_IN_HOUR) / MILLISSECONDS_IN_MINUTE);
  const seconds = Math.floor((interval % MILLISSECONDS_IN_MINUTE) / 1000);
  return ([days, hours, minutes, seconds] as const).map(t => t.toString().padStart(2, '0'));
}
